import { ArrowDownIcon, XMarkIcon } from '@heroicons/react/16/solid';
import {
  ClockIcon,
  CurrencyDollarIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  HandRaisedIcon,
  InformationCircleIcon,
  UserMinusIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import {
  ComponentType,
  SVGProps,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import {
  FillPool,
  FillReason,
  FillSignals,
  FillSignalsDetail,
} from '../api/types';
import { formatCents } from '../../PaySurge/format';
import useJobSurge from '../../PaySurge/hooks/useJobSurge';
import { dashboardLiveQueryOptions } from '../api/liveQuery';

// Smart Fill (GRAVY-10888) on the calendar card, the Day card and the Shift
// Detail panel, transcribed from Figma "Dashboard - Shift risk monitoring"
// (node 37530-68067, Aly, 2026-10-13):
//
//   week card   reason icons in the footer: two or fewer as icons, more as
//               the main reason's icon and "+N" (41823:126850, 41823:126823),
//               with the hover box listing them (41841:131843)
//   day card    the main reason as a red pill, then "Other reasons" as
//               outlined pills (42087:24098)
//   panel       the reason pills, "Pay surge · Tier 1 fired · next tier in
//               1 hr." (S16) and "Widen the pool · Round 2 of 4 · next round
//               in 1 hr" (42073:150589)
//
// Which reasons a card shows is the SERVER's answer (`on_card`, round 2 V7:
// within 48 hours of the start, Low supply always, never On track or "not
// enough signal yet"), so the banner counts and the reason filter — which use
// the same rule — always agree with what is on screen.

type Icon = ComponentType<SVGProps<SVGSVGElement>>;

/** One icon per reason code, as the design draws them. */
export const REASON_ICONS: Record<string, Icon> = {
  too_close: ClockIcon,
  low_supply: UserMinusIcon,
  low_exposure: UsersIcon,
  passing_on_pay: CurrencyDollarIcon,
  passing_other: HandRaisedIcon,
  reliability_risk: ExclamationTriangleIcon,
};

/** The banner's chips, in the design's order, with its fixed names (V3). */
export const BANNER_REASONS: { code: string; label: string }[] = [
  { code: 'low_exposure', label: 'Few invited' },
  { code: 'passing_on_pay', label: 'Passing on pay' },
  { code: 'passing_other', label: 'Passing' },
  { code: 'reliability_risk', label: 'Reliability risk' },
  { code: 'low_supply', label: 'Low supply' },
  { code: 'too_close', label: 'Starts soon' },
];

/**
 * A reason this build has no icon for (one a later server added; the contract
 * keeps the code open, review D-08) gets a generic one, and its own labels
 * from the server, rather than disappearing.
 */
export const ReasonIcon = ({ code, size }: { code: string; size: number }) => {
  const Svg = REASON_ICONS[code] ?? InformationCircleIcon;
  return <Svg aria-hidden="true" width={size} height={size} />;
};

/**
 * The reason's name on a card. Aly's V3 change (2026-10-14, GRAVY-10888
 * comment 61133): the non-pay reason is one word on the card, and "Passing:
 * Too far" stays in the hover box and the panel.
 */
export const cardLabel = (reason: FillReason): string =>
  reason.code === 'passing_other' ? 'Passing' : reason.short_label;

/**
 * The likely-to-fill value and expected to show, for the Product and Data
 * users on the percent flag only (decision 18): the server sends both as null
 * to everyone else, and the % as null for the first 2 hours after posting
 * (FS-1.3, `too_early`).
 *
 * No "Too early to tell" state (A2, Aly and Rav, 2026-10-15): ops learn two
 * bands, Critical and At risk, and the reasons under them. A new shift just
 * shows its band and whatever reasons it has.
 */
export const likelyToFillText = (
  signals?: FillSignals | null,
): string | null => {
  if (!signals) return null;
  const parts = [
    signals.likely_to_fill_pct != null &&
      `${signals.likely_to_fill_pct}% likely to fill`,
    // One decimal (FS-2.3): JSON turns the server's 3.0 into 3 (review D4-03).
    signals.expected_to_show != null &&
      `${signals.expected_to_show.toFixed(1)} expected to show`,
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
};

export const LikelyToFill = ({
  signals,
  testId,
}: {
  signals?: FillSignals | null;
  testId: string;
}) => {
  const text = likelyToFillText(signals);
  if (!text) return null;
  return (
    <span className="dashv2-likely" data-testid={testId}>
      {text}
    </span>
  );
};

/**
 * A Critical or At risk card with Smart Fill on but no reason keeps its band
 * and shows no icons; hovering it says so (Virna, 2026-10-15).
 *
 * "No reason" means only On track or "not enough signal yet", which never go
 * on a card. A real reason that graphy holds off the card until 2 days out
 * (V7, `on_card: false`) is still a reason, and Shift Detail names it, so the
 * hover must not say there is none (review D-01).
 */
export const NO_REASON_TEXT = 'No specific reason yet';

const NOT_A_REASON = new Set(['on_track', 'unknown']);

export const noReasonHint = (
  signals: FillSignals | null | undefined,
  band: string | null | undefined,
  smartFillOn = false,
): boolean => {
  if (band !== 'critical' && band !== 'at_risk') return false;
  // graphy sends null both with Smart Fill off and when the job has no row
  // yet (or a stale or withheld one), so only the page can tell them apart
  // (totals.fill_reasons). With it on, there is still no reason to give; with
  // it off the card stays as it was before Smart Fill (review K item 4, #29).
  if (!signals) return smartFillOn;
  const { others, ...main } = signals.diagnosis;
  return [main, ...(others ?? [])].every((r) => NOT_A_REASON.has(r.code));
};

/** The reasons a card shows, main first. Empty when there are none. */
export const cardReasons = (signals?: FillSignals | null): FillReason[] => {
  if (!signals) return [];
  const { others, ...main } = signals.diagnosis;
  return [main, ...(others ?? [])].filter((r) => r.on_card);
};

// ── Week card ──────────────────────────────────────────────────────────────

/**
 * The hover box. It is portalled to <body> with fixed positioning because the
 * card clips its content (`overflow: hidden`) and the box overlaps the next
 * column in the design.
 */
const ReasonTooltip = ({
  id,
  reasons,
  signals,
  anchor,
  tone,
}: {
  id: string;
  reasons: FillReason[];
  signals?: FillSignals | null;
  anchor: DOMRect;
  tone: string;
}) => {
  const [main, ...others] = reasons;
  const width = 180;
  // Above the icons, its right edge 4px past them and an 8px gap, as Aly's
  // updated prototype places it (2026-10-14). Below them when the box would
  // not fit above, with the prototype's height estimate (review D-03).
  const left = Math.max(
    8,
    Math.min(anchor.right + 4 - width, window.innerWidth - width - 8),
  );
  const below = anchor.top - 8 - (44 + reasons.length * 26) < 4;
  const place = below
    ? { top: anchor.bottom + 8 }
    : { bottom: window.innerHeight - anchor.top + 8 };
  return createPortal(
    <div className="dashv2">
      <div
        className={`dashv2-reasontip ${tone}`}
        id={id}
        role="tooltip"
        data-testid="fillReasonTooltip"
        style={{ ...place, left, width }}
      >
        <div className="dashv2-reasontip-main">
          <ReasonIcon code={main.code} size={20} />
          <span title={main.label}>{main.short_label}</span>
        </div>
        <LikelyToFill signals={signals} testId="fillReasonTooltipLikely" />
        {others.length > 0 && (
          <div className="dashv2-reasontip-others">
            <span className="dashv2-reasontip-head">Other reasons</span>
            {others.map((r) => (
              <div className="dashv2-reasontip-row" key={r.code}>
                <ReasonIcon code={r.code} size={18} />
                <span title={r.label}>{r.short_label}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

/*
 * Whether the page's last input was a pointer press rather than a key. A
 * card focused after a press (clicked, or handed focus back when Shift
 * Detail's close button was clicked) is not keyboard focus, so its box stays
 * shut. Read page-wide: the card's own mousedown was forgotten on blur, so a
 * mouse close reopened the box (review D4-01), and Safari never blurs a
 * clicked button, so a later Tab to it never opened it (review D4-02).
 */
let lastInputWasPointer = false;
let inputTrackers = 0;
const notePointer = () => {
  lastInputWasPointer = true;
};
const noteKey = () => {
  lastInputWasPointer = false;
};
const trackInput = () => {
  inputTrackers += 1;
  if (inputTrackers === 1) {
    document.addEventListener('mousedown', notePointer, true);
    document.addEventListener('keydown', noteKey, true);
  }
  return () => {
    inputTrackers -= 1;
    if (inputTrackers > 0) return;
    document.removeEventListener('mousedown', notePointer, true);
    document.removeEventListener('keydown', noteKey, true);
    lastInputWasPointer = false;
  };
};

/**
 * The card footer's reasons: up to two icons, otherwise the main reason's
 * icon and "+N" for the rest. Hover, or keyboard focus on the card, opens the
 * box listing them.
 */
export const CardFillReasons = ({
  signals,
  band,
}: {
  signals?: FillSignals | null;
  /** The card's fill band: amber on At risk, red otherwise (review D-05). */
  band?: string | null;
}) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  const tipId = useId();
  const reasons = cardReasons(signals);
  const hasReasons = reasons.length > 0;

  useEffect(() => {
    const host = ref.current?.closest<HTMLElement>('.dashv2-card');
    if (!host) return undefined;
    const untrack = trackInput();
    const show = () => {
      if (!ref.current || lastInputWasPointer) return;
      setAnchor(ref.current.getBoundingClientRect());
      host.setAttribute('aria-describedby', tipId);
    };
    const hide = () => {
      setAnchor(null);
      host.removeAttribute('aria-describedby');
    };
    host.addEventListener('focus', show);
    host.addEventListener('blur', hide);
    return () => {
      untrack();
      host.removeEventListener('focus', show);
      host.removeEventListener('blur', hide);
      // A refresh that drops the reasons unmounts the box (review D4-07).
      if (host.getAttribute('aria-describedby') === tipId) {
        host.removeAttribute('aria-describedby');
      }
    };
  }, [hasReasons, tipId]);

  // The box is fixed to where the icons were; close it when anything under
  // it scrolls, as the prototype does (review D-03), and on Escape, so it can
  // be dismissed without moving the pointer (review D-04).
  const isOpen = anchor !== null;
  // Escape or a scroll closes the box without a blur: stop describing the
  // card by a box that is gone (review D4-07).
  useEffect(() => {
    if (isOpen) return;
    const host = ref.current?.closest<HTMLElement>('.dashv2-card');
    if (host?.getAttribute('aria-describedby') === tipId) {
      host.removeAttribute('aria-describedby');
    }
  }, [isOpen, tipId]);
  useEffect(() => {
    if (!isOpen) return undefined;
    const off = () => setAnchor(null);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAnchor(null);
    };
    window.addEventListener('scroll', off, true);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', off, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [isOpen]);

  if (!hasReasons) return null;

  const tone = band === 'at_risk' ? 'tone-at-risk' : 'tone-critical';

  const shown = reasons.length <= 2 ? reasons : reasons.slice(0, 1);
  const more = reasons.length - shown.length;
  const open = () =>
    ref.current && setAnchor(ref.current.getBoundingClientRect());
  const close = () => setAnchor(null);

  return (
    <span
      ref={ref}
      className={`dashv2-cardreasons ${tone}`}
      data-testid="cardFillReasons"
      data-reasons={reasons.map((r) => r.code).join(' ')}
      aria-label={`Not filling: ${reasons.map((r) => r.short_label).join(', ')}`}
      role="img"
      onMouseEnter={open}
      onMouseLeave={close}
    >
      {shown.map((r) => (
        <ReasonIcon key={r.code} code={r.code} size={18} />
      ))}
      {more > 0 && <span className="dashv2-cardreasons-more">+{more}</span>}
      {anchor && (
        <ReasonTooltip
          id={tipId}
          reasons={reasons}
          signals={signals}
          anchor={anchor}
          tone={tone}
        />
      )}
    </span>
  );
};

// ── Day card and panel ─────────────────────────────────────────────────────

/**
 * The main reason as a red pill, then "Other reasons" as outlined pills —
 * smaller and quieter, so the one answer to "why isn't this filling?" stays
 * the main one (round 2 V1).
 */
export const ReasonPills = ({
  reasons,
  testId,
  onCard = false,
  full = false,
}: {
  reasons: FillReason[];
  testId: string;
  /** A card's pills use the card names ("Passing"). */
  onCard?: boolean;
  /** The Shift Detail panel says each reason as a full sentence (D9, Virna
      2026-10-15); cards and the hover box keep the short names. */
  full?: boolean;
}) => {
  if (reasons.length === 0) return null;
  const [main, ...others] = reasons;
  const name = (r: FillReason) =>
    onCard ? cardLabel(r) : full ? r.label : r.short_label;
  return (
    <div
      className={`dashv2-reasonpills${full ? ' is-full' : ''}`}
      data-testid={testId}
    >
      <span
        className="dashv2-reasonpill is-main"
        data-reason={main.code}
        title={main.label}
      >
        <ReasonIcon code={main.code} size={18} />
        {name(main)}
      </span>
      {others.length > 0 && (
        <span className="dashv2-reasonpills-others">
          <span className="dashv2-reasonpills-head">Other reasons</span>
          {others.map((r) => (
            <span
              className="dashv2-reasonpill"
              data-reason={r.code}
              key={r.code}
              title={r.label}
            >
              <ReasonIcon code={r.code} size={18} />
              {name(r)}
            </span>
          ))}
        </span>
      )}
    </div>
  );
};

/** A surge tier's state, in words for the stop's label and title. */
const TIER_STATE: Record<string, string> = {
  fired: 'applied',
  skipped: 'skipped',
  pending: 'not applied yet',
};

/** "$2" for whole dollars, "$1.50" otherwise, as the design writes them. */
const dollars = (cents: number) =>
  cents % 100 === 0 ? `$${cents / 100}` : `$${(cents / 100).toFixed(2)}`;

/**
 * What workers did with the invite (Aly's A8, 2026-10-14, Figma
 * 42101:24435): views and declines, the most cited decline reason, and the
 * pay against the market when it is below. Aly draws it in a separate
 * Hired/Invites panel, part of the wider redesign whose scope is not settled
 * (D9), so it sits above the Invites list in today's panel.
 */
export const WorkerResponse = ({
  signals,
}: {
  signals?: FillSignalsDetail | null;
}) => {
  const evidence = signals?.evidence;
  if (!evidence) return null;
  // Like graphy's fill reason (SmartFill::Diagnosis#named_reason), "Other"
  // is never named: the next reason is, or none (review D-06).
  const top =
    evidence.top_decline_reasons.find((r) => r.code !== 'other') ??
    evidence.top_decline_reasons[0];
  const topCode = top?.code === 'pay' ? 'passing_on_pay' : 'passing_other';
  const topLabel =
    top?.code === 'pay'
      ? 'Passing on pay'
      : top?.code === 'other'
        ? 'Passing'
        : `Passing: ${top?.label}`;
  const market = evidence.market_median_cents;
  const pay = evidence.pay_cents;
  const below = market != null && pay != null ? market - pay : 0;

  return (
    <div className="dashv2-workerresponse" data-testid="workerResponse">
      <div className="dashv2-workerresponse-stats">
        <span className="dashv2-workerresponse-head">Worker response</span>
        <span className="dashv2-workerresponse-counts">
          <span className="dashv2-workerresponse-count">
            <EyeIcon aria-hidden="true" width={16} height={16} />
            {evidence.viewed} viewed
          </span>
          <span className="dashv2-workerresponse-dot" aria-hidden="true" />
          <span className="dashv2-workerresponse-count is-declined">
            <XMarkIcon aria-hidden="true" width={16} height={16} />
            {evidence.declined} declined
          </span>
        </span>
        {top && (
          <span
            className="dashv2-workerresponse-top"
            data-testid="workerResponseTopReason"
            data-reason={top.code}
          >
            <ReasonIcon code={topCode} size={18} />
            <span>{topLabel}</span> <span>({top.count})</span>
          </span>
        )}
      </div>
      {below > 0 && (
        <span
          className="dashv2-workerresponse-pay"
          data-testid="workerResponsePay"
        >
          <ArrowDownIcon aria-hidden="true" width={16} height={16} />
          Pay is {dollars(below)}/hr below market
        </span>
      )}
    </div>
  );
};

/** Figma `griddy-icons:radar`, one per invite round. */
const RadarIcon = ({ className }: { className: string }) => (
  <svg
    className={className}
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M15.375 9.00003C15.375 12.5138 12.5137 15.375 9 15.375C5.48625 15.375 2.625 12.5138 2.625 9.00003C2.625 5.48628 5.48625 2.62503 9 2.62503C10.515 2.62503 11.9475 3.15003 13.0912 4.11003L11.4862 5.71503C10.7713 5.16858 9.89608 4.87332 8.99625 4.87503C7.90241 4.87562 6.85355 5.31041 6.08009 6.08387C5.30663 6.85733 4.87185 7.90619 4.87125 9.00003C4.87125 11.2763 6.72 13.125 8.99625 13.125C11.2725 13.125 13.1213 11.2763 13.1213 9.00003H11.9962C11.9962 10.6538 10.65 12 8.99625 12C7.3425 12 5.99625 10.6538 5.99625 9.00003C5.99715 8.45498 6.14634 7.92044 6.42784 7.4537C6.70933 6.98696 7.11251 6.60562 7.59419 6.35052C8.07586 6.09543 8.61787 5.9762 9.16213 6.00562C9.70638 6.03504 10.2324 6.212 10.6837 6.51753L9.285 7.91628C9.19114 7.88916 9.09395 7.87528 8.99625 7.87503C8.3775 7.87503 7.87125 8.38128 7.87125 9.00003C7.87125 9.61878 8.3775 10.125 8.99625 10.125C9.615 10.125 10.1212 9.61878 10.1212 9.00003C10.1212 8.89878 10.1025 8.80503 10.08 8.71128L14.6962 4.09503L14.2988 3.69753C13.6042 2.9987 12.7779 2.44464 11.8677 2.06743C10.9575 1.69022 9.98151 1.49736 8.99625 1.50003C4.86375 1.50003 1.5 4.86378 1.5 9.00003C1.5 13.1363 4.86375 16.5 9 16.5C13.1362 16.5 16.5 13.1363 16.5 9.00003H15.375Z" />
  </svg>
);

/**
 * The panel's "Widen the pool" (round 2 V5): display only, no switch. The
 * label is the server's ("Round 2 of 4 · next round in 1 hr", "… sent early
 * …"); the bar fills to the latest round.
 */
export const PoolStatus = ({ pool }: { pool?: FillPool | null }) => {
  if (!pool) return null;
  const total = Math.max(pool.rounds_total, 2);
  const filledPct =
    Math.max(0, Math.min(1, (pool.round - 1) / (total - 1))) * 100;
  return (
    <div className="dashv2-panel-sfsection" data-testid="panelPool">
      <div className="dashv2-panel-sfhead">
        <span className="dashv2-panel-sftitle">Widen the pool</span>
        <span className="dashv2-panel-sfsub" data-testid="panelPoolLabel">
          {pool.label.split(' · ').map((part, i) => (
            <span key={part}>
              {i > 0 && <span className="dashv2-panel-sfdot" />}
              {part}
            </span>
          ))}
        </span>
      </div>
      <div
        className="dashv2-poolbar"
        role="progressbar"
        aria-label="Invite rounds sent"
        aria-valuemin={0}
        aria-valuemax={pool.rounds_total}
        aria-valuenow={pool.round}
      >
        <div className="dashv2-poolbar-track">
          {pool.round > 0 && (
            <>
              <div
                className="dashv2-poolbar-fill"
                style={{ width: `${filledPct}%` }}
              />
              <span
                className="dashv2-poolbar-handle"
                style={{ left: `${filledPct}%` }}
              />
            </>
          )}
        </div>
        <div className="dashv2-poolbar-steps">
          {Array.from({ length: pool.rounds_total }, (_, i) => {
            const n = i + 1;
            const state =
              n < pool.round ? 'done' : n === pool.round ? 'current' : 'next';
            return (
              <RadarIcon
                key={n}
                className={`dashv2-poolbar-step is-${state}`}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

/**
 * The Pay surge line from the job's schedule alone, in Aly's words: a tier
 * is numbered by its place in the schedule (1 fires first), so a skipped
 * one keeps its number.
 */
const scheduleLine = (paused: boolean, lastFired: number): string => {
  if (paused) return 'Surge paused.';
  return lastFired >= 0 ? `Tier ${lastFired + 1} fired.` : 'No tier fired.';
};

/**
 * The panel's "Pay surge" (Figma 42073:150589), display only (round 2: guides,
 * no actions). The line under the title is Shift Detail's `surge_step`; the
 * bar is the job's own schedule from the surge API the Job page uses
 * (GRAVY-10443): one stop per tier, the applied ones green, the bar filled to
 * the last one applied.
 *
 * The line is the surge's own, never the card's `next_step`, which is
 * whichever comes first, a tier or an invite round: reading that left the
 * line blank while a round was due first (Aly, 2026-10-19: a round never
 * changes this line; review RT-05).
 *
 * Product, Jira GRAVY-10888 (S16): Aly's words, never blank. The server
 * writes them ("Tier 2 fired · next tier in 1 hr.", "Tier 2 fired.",
 * "Next tier in 1 hr.", "Peter B. paused surge.", "Surge paused.", "No tier
 * fired."; SmartFill::Builder#surge_step). Without one (an older server),
 * the words that need no countdown come from the schedule (#scheduleLine).
 */
export const SurgeStatus = ({
  jobId,
  signals,
}: {
  jobId: number;
  signals?:
    | (FillSignals & Pick<Partial<FillSignalsDetail>, 'surge_step'>)
    | null;
}) => {
  // Shift Detail's own refresh: the Pay surge line comes with it (D7-04).
  const { data: surge } = useJobSurge(jobId, !!signals, {
    staleTime: dashboardLiveQueryOptions.staleTime,
    refetchInterval: dashboardLiveQueryOptions.refetchInterval,
    refetchOnWindowFocus: dashboardLiveQueryOptions.refetchOnWindowFocus,
  });
  const surgeStep = signals?.surge_step ?? null;
  const tiers = surge && surge.status !== 'off' ? surge.tiers : [];
  if (!signals || (!surgeStep && tiers.length === 0)) return null;

  const paused =
    surge?.status === 'paused' || surgeStep?.type === 'surge_paused';
  // Tiers fire from the longest lead time to the shortest.
  const ordered = [...tiers].sort(
    (a, b) => b.lead_time_minutes - a.lead_time_minutes,
  );
  const fired = ordered.filter((t) => t.state === 'fired').length;
  const lastFired = ordered.map((t) => t.state).lastIndexOf('fired');
  const label = surgeStep?.label ?? scheduleLine(paused, lastFired);
  const filledPct =
    (Math.max(0, lastFired) / (Math.max(ordered.length, 2) - 1)) * 100;

  return (
    <div className="dashv2-panel-sfsection" data-testid="panelSurgeNext">
      <div className="dashv2-panel-sfhead">
        <span className="dashv2-panel-sftitle">Pay surge</span>
        <span className={`dashv2-panel-sfsub${paused ? ' is-muted' : ''}`}>
          {label}
        </span>
      </div>
      {ordered.length > 0 && (
        <div className="dashv2-surgebar" data-testid="panelSurgeBar">
          <div className="dashv2-poolbar-track">
            {fired > 0 && (
              <>
                <div
                  className="dashv2-poolbar-fill"
                  style={{ width: `${filledPct}%` }}
                />
                <span
                  className="dashv2-surgebar-handle"
                  style={{ left: `${filledPct}%` }}
                >
                  <CurrencyDollarIcon aria-hidden="true" />
                </span>
              </>
            )}
          </div>
          <div className="dashv2-surgebar-steps">
            {ordered.map((t) => {
              // In words as well as colour (review D-04). role="img" is what
              // exposes the aria-label: a bare span is generic and its name is
              // ignored, as ShiftCard's lock icon notes (review N-07).
              const said = `+${formatCents(t.rate_cents)}, ${TIER_STATE[t.state] ?? t.state}`;
              return (
                <span
                  key={t.lead_time_minutes}
                  className={`dashv2-surgebar-step is-${t.state}`}
                  role="img"
                  aria-label={said}
                  title={said}
                >
                  +{formatCents(t.rate_cents)}
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
